import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { statsAPI } from "../services/api";
import BudgetChart from "../components/report/BudgetChart";
import { motion } from "framer-motion";

const ReportPage = () => {
  const [stats, setStats] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsRes, budgetRes] = await Promise.all([
          statsAPI.getDashboard(),
          statsAPI.getBudgetChart(),
        ]);
        setStats(statsRes.data.data);
        setBudgetData(budgetRes.data.data);
      } catch (error) {
        console.error("Failed to fetch report data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = [
    {
      icon: "fas fa-project-diagram",
      value: stats?.totalProjects || 0,
      label: "โครงการทั้งหมด",
      color: "#4e73df",
      bgColor: "#4e73df20",
      borderColor: "#4e73df",
    },
    {
      icon: "fas fa-brain",
      value: stats?.totalKnowledge || 0,
      label: "องค์ความรู้",
      color: "#1cc88a",
      bgColor: "#1cc88a20",
      borderColor: "#1cc88a",
    },
    {
      icon: "fas fa-newspaper",
      value: stats?.totalNews || 0,
      label: "ข่าวประชาสัมพันธ์",
      color: "#36b9cc",
      bgColor: "#36b9cc20",
      borderColor: "#36b9cc",
    },
    {
      icon: "fas fa-users",
      value: stats?.totalMembers || 0,
      label: "บุคลากร",
      color: "#f6c23e",
      bgColor: "#f6c23e20",
      borderColor: "#f6c23e",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>รายงาน | งานบริการวิชาการ มหาวิทยาลัยราชภัฏศรีสะเกษ</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-blue-50 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 rounded-full mb-6 shadow-lg">
              <i className="fas fa-chart-bar text-3xl text-primary-500"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary-500 mb-4 relative inline-block pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-secondary-500 after:rounded-full">
              รายงาน
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-500 rounded-full"></span>
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              สถิติและข้อมูลการดำเนินงานบริการวิชาการ
              เพื่อการพัฒนาท้องถิ่นอย่างยั่งยืน
            </p>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          >
            {statCards.map((stat, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.03, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all"
                style={{ borderLeft: `4px solid ${stat.color}` }}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                      style={{
                        backgroundColor: stat.bgColor,
                        color: stat.color,
                      }}
                    >
                      <i className={stat.icon}></i>
                    </div>
                    <span className="text-3xl font-bold text-gray-800">
                      {stat.value.toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-gray-600">
                    {stat.label}
                  </div>
                  <div className="mt-2 w-full bg-gray-100 rounded-full h-1">
                    <div
                      className="h-1 rounded-full"
                      style={{
                        width: `${Math.min(100, (stat.value / 100) * 100)}%`,
                        backgroundColor: stat.color,
                      }}
                    ></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Budget Charts */}
          {budgetData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mb-12"
            >
              <BudgetChart data={budgetData} />
            </motion.div>
          )}

          {/* Projects by Fiscal Year */}
          {stats?.projectsByFiscalYear &&
            stats.projectsByFiscalYear.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 mb-8"
              >
                <h2 className="text-2xl font-semibold text-primary-500 mb-6 flex items-center">
                  <span className="w-10 h-10 bg-secondary-100 rounded-xl flex items-center justify-center mr-3">
                    <i className="fas fa-calendar-alt text-secondary-500"></i>
                  </span>
                  โครงการแยกตามปีงบประมาณ
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {stats.projectsByFiscalYear.map((item, index) => (
                    <motion.div
                      key={item.fiscal_year}
                      whileHover={{ scale: 1.02, y: -3 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      className="relative bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-6 text-white overflow-hidden group"
                    >
                      {/* Decorative elements */}
                      <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500"></div>
                      <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8 group-hover:scale-150 transition-transform duration-500"></div>

                      <div className="relative z-10">
                        <div className="text-4xl font-bold mb-2">
                          {item.project_count}
                        </div>
                        <div className="text-lg font-medium opacity-90">
                          โครงการ
                        </div>
                        <div className="mt-4 text-sm opacity-75 flex items-center">
                          <i className="fas fa-calendar mr-2"></i>
                          ปีงบประมาณ {item.fiscal_year}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

          {/* Additional Stats */}
          {stats?.totalVisitors > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <i className="fas fa-eye text-2xl text-blue-500"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      สถิติการเข้าชม
                    </h3>
                    <p className="text-3xl font-bold text-primary-500">
                      {stats.totalVisitors.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      ผู้เข้าชมทั้งหมด
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">อัตราการเข้าชมเฉลี่ย</span>
                    <span className="font-semibold text-primary-500">
                      {(stats.totalVisitors / 30 || 0).toFixed(0)} ครั้ง/วัน
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                    <i className="fas fa-chart-line text-2xl text-green-500"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      สถิติการดำเนินงาน
                    </h3>
                    <p className="text-3xl font-bold text-primary-500">
                      {stats?.totalProjects || 0}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">โครงการทั้งหมด</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">โครงการที่ดำเนินการ</span>
                    <span className="font-semibold text-green-500">
                      {Math.round((stats?.totalProjects || 0) * 0.8)} โครงการ
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReportPage;
